import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import Global from '../store/global-selectors';
import { USD } from '../constants/currency-codes';
import { InsuranceProviders } from '../enums/insurance-providers';
import { SellerSettings } from '../enums/seller-settings/seller-settings';
import { CA, US } from '../constants/country-codes';
import { useSellerWallets } from '../hooks/use-seller-wallets';
import { updateSellerSettingsBulk } from '../pages/settings/api/save-settings';
import { saveNewSellerSettings } from '../store/seller/seller.actions';
import { LogService } from './log-service';
import { selectActiveNonWalletedSellerLabelProviders } from '../hooks/use-seller-label-providers';
import { FeatureFlagDeclarations } from '../feature-flags/feature-flag-declarations';
import { FeatureFlags } from '../feature-flags/feature-flags';
import { Providers } from '../enums/providers';

export const noShipsuranceBillingInfoErrors = ['Unable to find billing information', 'No billing account found'];

// Functions that begin with `use` are React hooks that can be used by components
// Functions that begin with `_` are private functions that are not exported
// Functions that do not begin with `_` or `use` are to be used in epics or selectors

// Functions named `is*Eligible` are used for insurance providers that are conditionally available. For walleted
//    insurance providers, this ideally means that the provider is available for the seller, but is not yet set up to
//    make purchases.
// Functions named `is*Activated` are used for walleted insurance providers that have been set up to make purchases.
// Functions named `canShow*` determine if the insurance provider should be shown in the UI to make purchases.

const _isXCoverEligible = ({ wallets }) =>
  wallets.some((wallet) => wallet?.newInsuranceProviderIds?.includes(InsuranceProviders.XCover.insuranceProviderId));
const _isXCoverActivated = ({ wallets }) =>
  wallets.some((wallet) => wallet?.resources?.insuranceProviderIds?.includes(InsuranceProviders.XCover.insuranceProviderId));
export const isXCoverActivated = (state) => {
  const wallets = Global.selectSellerWallets(state);
  return _isXCoverActivated({ wallets });
};
export const useIsXCoverActivated = () => {
  const wallets = useSellerWallets();
  return _isXCoverActivated({ wallets });
};

const _canShowXCover = (data) => _isXCoverEligible(data) || _isXCoverActivated(data);
export const canShowXCover = (state) => {
  const wallets = Global.selectSellerWallets(state);
  return _canShowXCover({ wallets });
};
export const useCanShowXCover = () => {
  const wallets = useSellerWallets();
  return _canShowXCover({ wallets });
};

const _isParcelGuardEligible = ({ wallets }) =>
  wallets.some((wallet) => wallet?.newInsuranceProviderIds?.includes(InsuranceProviders.ParcelGuard.insuranceProviderId));

const _isParcelGuardActivated = ({ wallets }) =>
  wallets.some((wallet) => wallet?.resources?.insuranceProviderIds?.includes(InsuranceProviders.ParcelGuard.insuranceProviderId));
export const isParcelGuardActivated = (state) => {
  const wallets = Global.selectSellerWallets(state);
  return _isParcelGuardActivated({ wallets });
};
export const useIsParcelGuardActivated = () => {
  const wallets = useSellerWallets();
  return _isParcelGuardActivated({ wallets });
};

const _canShowParcelGuard = (data) => _isParcelGuardEligible(data) || _isParcelGuardActivated(data);

export const canShowParcelGuard = (state) => {
  const wallets = Global.selectSellerWallets(state);
  return _canShowParcelGuard({ wallets });
};
export const useCanShowParcelGuard = () => {
  const wallets = useSellerWallets();
  return _canShowParcelGuard({ wallets });
};

export const useWalletedInsuranceProviderId = () => {
  const canShowParcelGuardVal = useCanShowParcelGuard();
  const canShowXCoverEligibleVal = useCanShowXCover();
  return useMemo(() => {
    if (canShowParcelGuardVal) {
      return InsuranceProviders.ParcelGuard.insuranceProviderId;
    }
    if (canShowXCoverEligibleVal) {
      return InsuranceProviders.XCover.insuranceProviderId;
    }
    return null;
  }, [canShowParcelGuardVal, canShowXCoverEligibleVal]);
};
export const useWalletedInsuranceActivated = () => {
  const isParcelGuardActivatedVal = useIsParcelGuardActivated();
  const isXCoverActivatedVal = useIsXCoverActivated();
  return useMemo(() => {
    return isParcelGuardActivatedVal || isXCoverActivatedVal;
  }, [isParcelGuardActivatedVal, isXCoverActivatedVal]);
};
const canShowWalletedInsurance = (state) => {
  const canShowParcelGuardVal = canShowParcelGuard(state);
  const canShowXCoverVal = canShowXCover(state);
  return canShowParcelGuardVal || canShowXCoverVal;
};
export const useCanShowWalletedInsurance = () => {
  const canShowParcelGuardVal = useCanShowParcelGuard();
  const canShowXCoverVal = useCanShowXCover();
  return useMemo(() => {
    return canShowParcelGuardVal || canShowXCoverVal;
  }, [canShowParcelGuardVal, canShowXCoverVal]);
};

const SHIPSURANCE_INCLUDED_COUNTRIES = [US];

const _isShipsuranceEligible = ({ sellerHomeCountry }) => {
  return SHIPSURANCE_INCLUDED_COUNTRIES.includes(sellerHomeCountry);
};
export const isShipsuranceEligible = (state) => {
  const sellerHomeCountry = Global.selectSellerHomeCountry(state);
  return _isShipsuranceEligible({ sellerHomeCountry });
};
export const useIsShipsuranceEligible = () => {
  const sellerHomeCountry = useSelector(Global.selectSellerHomeCountry);
  return _isShipsuranceEligible({ sellerHomeCountry });
};

const _canShowShipsurance = ({ sellerHomeCountry, wallets }) => {
  const hasShipsuranceAccessVal = _isShipsuranceEligible({ sellerHomeCountry });
  const canShowParcelGuardVal = _canShowParcelGuard({ wallets });
  return hasShipsuranceAccessVal && !canShowParcelGuardVal;
};
export const canShowShipsurance = (state) => {
  const sellerHomeCountry = Global.selectSellerHomeCountry(state);
  const wallets = Global.selectSellerWallets(state);
  return _canShowShipsurance({ sellerHomeCountry, wallets });
};
export const useCanShowShipsurance = () => {
  const sellerHomeCountry = useSelector(Global.selectSellerHomeCountry);
  const wallets = useSellerWallets();
  return _canShowShipsurance({ sellerHomeCountry, wallets });
};

const COUNTRIES_THAT_CAN_FILTER_ON_SHIPSURANCE = [US, CA];
export const canShowShipsuranceForShipments = (state) => {
  const sellerHomeCountry = Global.selectSellerHomeCountry(state);
  return COUNTRIES_THAT_CAN_FILTER_ON_SHIPSURANCE.includes(sellerHomeCountry);
};

const isCarrierInsuranceSellerSettingEnabled = (state) => {
  const settingName = SellerSettings.insuranceCarrierEnabled.key;
  return Global.selectSellerSellerSetting(state, { settingName }) === 'true';
};
export const useCarrierInsuranceSellerSetting = () => {
  const settingValue = useSelector((state) =>
    Global.selectSellerSellerSetting(state, {
      settingName: SellerSettings.insuranceCarrierEnabled.key,
    }),
  );
  return settingValue === 'true';
};

export const shouldHideNonWalletInsuranceOverride = (state) => {
  const featureFlags = state.featureFlags;
  const featureFlagService = new FeatureFlags({ featureFlags });
  const cutOffDateString = featureFlagService.getFeatureFlag(FeatureFlagDeclarations.cutOffDateForHidingNonWalletInsurance);
  const sellerCreateDateUTCString = Global.selectSellerCreateDateUTC(state);
  const activeNonWalletSellerProviders = selectActiveNonWalletedSellerLabelProviders(state);
  const hasConnectedNonWalletedProviders = activeNonWalletSellerProviders.length > 0;

  if (!cutOffDateString) {
    return false;
  }

  try {
    const cutOffDate = new Date(cutOffDateString);
    const isNewSeller = new Date(sellerCreateDateUTCString) > cutOffDate;
    return isNewSeller && !hasConnectedNonWalletedProviders;
  } catch (e) {
    const sellerId = Global.selectSellerSellerId(state);
    LogService.logError(new Error('Failed to parse/compare seller create date for hiding non-wallet insurance', { cause: e }), {
      sellerId,
    });
    return false;
  }
};

const _isFedexUKWalletProvider = (sellerLabelProviderId, sellerLabelProviders) => {
  const selectedProvider = sellerLabelProviders.find(
    (provider) => String(provider.sellerLabelProviderId) === String(sellerLabelProviderId),
  );
  return selectedProvider?.labelProviderId === Providers.FedexUKWalleted.labelProviderId;
};

// Check if the currently selected provider is FedEx UK Wallet
const isSelectedProviderFedExUKWallet = (state, sellerLabelProviderId) => {
  if (!sellerLabelProviderId) {
    return false;
  }
  const sellerLabelProviders = Global.selectSellerLabelProviders(state);
  return _isFedexUKWalletProvider(sellerLabelProviderId, sellerLabelProviders);
};

const useIsSelectedProviderFedExUKWallet = (sellerLabelProviderId) => {
  const sellerLabelProviders = useSelector(Global.selectSellerLabelProviders);
  return useMemo(() => {
    if (!sellerLabelProviderId) {
      return false;
    }
    return _isFedexUKWalletProvider(sellerLabelProviderId, sellerLabelProviders);
  }, [sellerLabelProviders, sellerLabelProviderId]);
};

const canShowCarrierInsurance = (state, sellerLabelProviderId) => {
  const isCarrierInsuranceEnabled = isCarrierInsuranceSellerSettingEnabled(state);
  const canShowWalletedInsuranceVal = canShowWalletedInsurance(state);
  const shouldHide = shouldHideNonWalletInsuranceOverride(state);
  const isSelectedFedExUKWallet = isSelectedProviderFedExUKWallet(state, sellerLabelProviderId);

  // Hide carrier insurance if currently selected service is FedEx UK Wallet (not supported)
  if (isSelectedFedExUKWallet) {
    return false;
  }

  // Regardless of chosen settings or settings override logic, if you don't have access to walleted insurance
  // We want to display the carrier insurance option
  if (!canShowWalletedInsuranceVal) {
    return true;
  }

  if (shouldHide) {
    return false;
  }

  return isCarrierInsuranceEnabled;
};
const useCanShowCarrierInsurance = (sellerLabelProviderId) => {
  const isCarrierInsuranceEnabled = useCarrierInsuranceSellerSetting();
  const canShowWalletedInsuranceVal = useCanShowWalletedInsurance();
  const shouldHide = useSelector(shouldHideNonWalletInsuranceOverride);
  const isSelectedFedExUKWallet = useIsSelectedProviderFedExUKWallet(sellerLabelProviderId);

  return useMemo(() => {
    // Hide carrier insurance if currently selected service is FedEx UK Wallet (not supported)
    if (isSelectedFedExUKWallet) {
      return false;
    }

    // Regardless of chosen settings or settings override logic, if you don't have access to walleted insurance
    // We want to display the carrier insurance option
    if (!canShowWalletedInsuranceVal) {
      return true;
    }

    if (shouldHide) {
      return false;
    }

    return isCarrierInsuranceEnabled;
  }, [isCarrierInsuranceEnabled, canShowWalletedInsuranceVal, shouldHide, isSelectedFedExUKWallet]);
};

const isExternalInsuranceSellerSettingEnabled = (state) => {
  const settingName = SellerSettings.insuranceExternalEnabled.key;
  return Global.selectSellerSellerSetting(state, { settingName }) === 'true';
};
export const useExternalInsuranceSellerSetting = () => {
  const settingValue = useSelector((state) =>
    Global.selectSellerSellerSetting(state, {
      settingName: SellerSettings.insuranceExternalEnabled.key,
    }),
  );
  return settingValue === 'true';
};
// We only want to hide this option if the seller has a walleted insurance provider
const canShowExternalInsurance = (state) => {
  const isExternalInsuranceEnabled = isExternalInsuranceSellerSettingEnabled(state);
  const canShowWalletedInsuranceVal = canShowWalletedInsurance();
  const shouldHide = shouldHideNonWalletInsuranceOverride(state);

  // Regardless of chosen settings or settings override logic, if you don't have access to walleted insurance
  // We want to display the external insurance option
  if (!canShowWalletedInsuranceVal) {
    return true;
  }

  if (shouldHide) {
    return false;
  }

  return isExternalInsuranceEnabled;
};
const useCanShowExternalInsurance = () => {
  const isExternalInsuranceEnabled = useExternalInsuranceSellerSetting();
  const canShowWalletedInsuranceVal = useCanShowWalletedInsurance();
  const shouldHide = useSelector(shouldHideNonWalletInsuranceOverride);

  return useMemo(() => {
    // Regardless of chosen settings or settings override logic, if you don't have access to walleted insurance
    // We want to display the external insurance option
    if (!canShowWalletedInsuranceVal) {
      return true;
    }

    if (shouldHide) {
      return false;
    }

    return isExternalInsuranceEnabled;
  }, [isExternalInsuranceEnabled, canShowWalletedInsuranceVal, shouldHide]);
};

export const selectAvailableInsuranceOptions = ({ state }) => {
  const eligibilityPredicateMap = {
    [InsuranceProviders.None.insuranceProviderId]: true,
    [InsuranceProviders.Shipsurance.insuranceProviderId]: canShowShipsurance(state),
    [InsuranceProviders.XCover.insuranceProviderId]: canShowXCover(state),
    [InsuranceProviders.ParcelGuard.insuranceProviderId]: canShowParcelGuard(state),
    [InsuranceProviders.Carrier.insuranceProviderId]: canShowCarrierInsurance(state),
    [InsuranceProviders.External.insuranceProviderId]: canShowExternalInsurance(state),
  };
  return Object.values(InsuranceProviders)
    .filter(({ insuranceProviderId }) => eligibilityPredicateMap[insuranceProviderId])
    .map(({ insuranceProviderId, messageDescriptor, name, insurancePromoEnabledMessageDescriptor }) => ({
      insuranceOptionId: insuranceProviderId,
      messageDescriptor,
      name,
      insurancePromoEnabledMessageDescriptor,
    }));
};

export const selectShipmentInsuranceFilterOptions = ({ state }) => {
  const filterablePredicateMap = {
    [InsuranceProviders.None.insuranceProviderId]: true,
    [InsuranceProviders.Shipsurance.insuranceProviderId]: canShowShipsuranceForShipments(state),
    [InsuranceProviders.XCover.insuranceProviderId]: canShowXCover(state),
    [InsuranceProviders.ParcelGuard.insuranceProviderId]: canShowParcelGuard(state),
    [InsuranceProviders.Carrier.insuranceProviderId]: true,
    [InsuranceProviders.External.insuranceProviderId]: true,
  };
  return Object.values(InsuranceProviders)
    .filter(({ insuranceProviderId }) => filterablePredicateMap[insuranceProviderId])
    .map(({ insuranceProviderId, messageDescriptor, name, insurancePromoEnabledMessageDescriptor }) => ({
      insuranceOptionId: insuranceProviderId,
      messageDescriptor,
      name,
      insurancePromoEnabledMessageDescriptor,
    }));
};

export const useAvailableInsuranceOptions = (sellerLabelProviderId) => {
  const canShowShipsuranceVal = useCanShowShipsurance();
  const canShowXCoverVal = useCanShowXCover();
  const canShowParcelGuardVal = useCanShowParcelGuard();
  const canShowCarrierInsuranceVal = useCanShowCarrierInsurance(sellerLabelProviderId);
  const canShowExternalInsuranceVal = useCanShowExternalInsurance();

  const eligibilityMap = {
    [InsuranceProviders.None.insuranceProviderId]: true,
    [InsuranceProviders.Shipsurance.insuranceProviderId]: canShowShipsuranceVal,
    [InsuranceProviders.XCover.insuranceProviderId]: canShowXCoverVal,
    [InsuranceProviders.ParcelGuard.insuranceProviderId]: canShowParcelGuardVal,
    [InsuranceProviders.Carrier.insuranceProviderId]: canShowCarrierInsuranceVal,
    [InsuranceProviders.External.insuranceProviderId]: canShowExternalInsuranceVal,
  };

  return Object.values(InsuranceProviders)
    .filter(({ insuranceProviderId }) => eligibilityMap[insuranceProviderId])
    .map(({ insuranceProviderId, messageDescriptor, name, insurancePromoEnabledMessageDescriptor }) => ({
      insuranceOptionId: insuranceProviderId,
      messageDescriptor,
      name,
      insurancePromoEnabledMessageDescriptor,
    }));
};

export const useDoesInsuranceRequireSetUp = (insuranceProviderId) => {
  const isXCoverActivatedVal = useIsXCoverActivated();
  const isParcelGuardActivatedVal = useIsParcelGuardActivated();
  const registrationMap = {
    [InsuranceProviders.XCover.insuranceProviderId]: isXCoverActivatedVal,
    [InsuranceProviders.ParcelGuard.insuranceProviderId]: isParcelGuardActivatedVal,
  };
  return !(registrationMap[insuranceProviderId] ?? true);
};

export const calculateTotalInsuredValue = (packages = [], insuranceProviderId, sellerCurrencyCode) => {
  const totalInsuredAmount = packages.reduce((sum, p) => {
    return sum + (p?.insuredValue?.value ?? 0);
  }, 0);
  const insuredValueCurrencyCode = packages[0]?.insuredValue?.code || sellerCurrencyCode;
  const insuredValue = {
    value: totalInsuredAmount || 0,
    code: insuranceProviderId === InsuranceProviders.Shipsurance.insuranceProviderId ? USD : insuredValueCurrencyCode,
  };
  return insuredValue;
};

const insuranceSystemAutomationMap = {
  [InsuranceProviders.XCover.insuranceProviderId]: SellerSettings.insuranceSystemAutomationApplyXCover.key,
  [InsuranceProviders.ParcelGuard.insuranceProviderId]: SellerSettings.insuranceSystemAutomationApplyParcelGuard.key,
};

export const getAutoInsureSettingKey = (insuranceProviderId) => insuranceSystemAutomationMap[insuranceProviderId] ?? '';

export const useIsParcelGuardAutoInsureActive = () => {
  const isAutoInsureActive = useSelector(
    (state) =>
      Global.selectSellerSellerSetting(state, { settingName: SellerSettings.insuranceSystemAutomationApplyParcelGuard.key }) === 'true',
  );

  return isAutoInsureActive;
};

export const useIsXCoverAutoInsureActive = () => {
  const isAutoInsureActive = useSelector(
    (state) => Global.selectSellerSellerSetting(state, { settingName: SellerSettings.insuranceSystemAutomationApplyXCover.key }) === 'true',
  );

  return isAutoInsureActive;
};

export const useShouldAutoInsure = (insuranceProviderId) => {
  const dispatch = useDispatch();
  const settingName = insuranceSystemAutomationMap[insuranceProviderId] ?? '';
  const shouldAutoInsure = useSelector((state) => Global.selectSellerSellerSetting(state, { settingName }) === 'true');
  const setShouldAutoInsure = useCallback(
    async (newShouldAutoInsureValue) => {
      if (settingName === '') {
        LogService.logError(new Error('Failed to find insurance system in insuranceSystemAutomationMap'), { insuranceProviderId });
        return;
      }
      const sellerSettings = { [settingName]: newShouldAutoInsureValue };
      try {
        const newSettings = await updateSellerSettingsBulk(sellerSettings);
        dispatch(saveNewSellerSettings(newSettings));
      } catch (error) {
        LogService.logError(error);
        NotificationService.showNotification({
          title: '',
          content: (
            <FormattedMessage
              defaultMessage='Unable to save insurance setting. Please try again later.'
              description='Error message when unable to save insurance setting'
              id='YtkDjq'
            />
          ),
          notificationType: NOTIFICATION_TYPE_ERROR,
          duration: 3000,
        });
      }
    },
    [dispatch, settingName, insuranceProviderId],
  );
  return useMemo(() => ({ shouldAutoInsure, setShouldAutoInsure }), [shouldAutoInsure, setShouldAutoInsure]);
};
